import { insiderMembershipState } from "@faire/retailer-shared/lib/retailer";
import {
  useIsLoggedInRetailer,
  useIsLoggedInRetailerOrBrandPreview,
} from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getMarketEventMatchedDiscountBps } from "@faire/retailer-visitor-shared/serialized-data/getMarketEventMatchedDiscountBps";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { useEventsStore } from "@faire/retailer-visitor-shared/stores/domain/Events/shared/EventsStore";
import { exhaustiveSwitchWithDefault } from "@faire/web--source/common/exhaustiveSwitchWithDefault";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import {
  MARKET_FALLBACKS,
  getFaireMarketUtils,
} from "@faire/web--source/common/promotional-events/getFaireMarketUtils";
import { timestampToMillis } from "@faire/web--source/common/timestamp";
import { useClock } from "@faire/web--source/common/useClock";
import { PromotionalEventType } from "@faire/web-api--source/indigofair/common/PromotionalEventType";
import { IBrandPromoCollection } from "@faire/web-api--source/indigofair/data/IBrandPromoCollection";
import { ICurrentPromotionalEventResponse } from "@faire/web-api--source/indigofair/data/ICurrentPromotionalEventResponse";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import marketSpecials from "@faire/web-api--source/routes/www/markets/event_type/all-specials";
import { toZonedTime } from "date-fns-tz";
import { differenceInDays } from "date-fns/differenceInDays";
import { isWithinInterval } from "date-fns/isWithinInterval";
import { useState, useMemo, useCallback } from "react";

import { MarketState, PT_TIMEZONE, RetailerSegment } from "./types";

export const useMarketStore = () => {
  const { strictLocalize } = useStrictLocalization();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  const { nextOrActive, eventData: _eventData } = useEventsStore([
    "nextOrActive",
    "eventData",
  ]);

  const { now } = useClock();

  const latestMarketEvent = useMemo(() => {
    const maybeEvent = nextOrActive;
    if (
      maybeEvent?.event?.event_group ===
      ICurrentPromotionalEventResponse.IPromotionalEvent.PromotionalEventGroup
        .MARKET
    ) {
      return maybeEvent;
    }
    return undefined;
  }, [nextOrActive]);

  const currentEventType = useMemo(() => {
    return (
      (latestMarketEvent?.event?.event_type as PromotionalEventType) ??
      MARKET_FALLBACKS(strictLocalize).currentEventType
    );
  }, [latestMarketEvent, strictLocalize]);

  const eventData = useMemo(() => {
    return (
      _eventData(currentEventType) ??
      ICurrentPromotionalEventResponse.build({
        start_at: 0,
        end_at: 0,
        insider_early_access_start_at: 0,
      })
    );
  }, [currentEventType, _eventData]);

  const marketName = useMemo(() => {
    return (
      latestMarketEvent?.event?.event_name ??
      MARKET_FALLBACKS(strictLocalize).name
    );
  }, [latestMarketEvent, strictLocalize]);

  const marketYear = useMemo(() => {
    return latestMarketEvent?.start_at
      ? new Date(latestMarketEvent.start_at).getFullYear().toString()
      : undefined;
  }, [latestMarketEvent]);

  const latestMarketEarlyAccessRangeDate = useMemo(() => {
    const { earlyAccessRangeDate } = getFaireMarketUtils(
      marketYear,
      strictLocalize
    );
    return earlyAccessRangeDate(strictLocalize, latestMarketEvent);
  }, [marketYear, strictLocalize, latestMarketEvent]);

  const latestMarketEarlyAccessRangeDateWithYear = useMemo(() => {
    const { earlyAccessRangeDateWithYear } = getFaireMarketUtils(
      marketYear,
      strictLocalize
    );
    return earlyAccessRangeDateWithYear(strictLocalize, latestMarketEvent);
  }, [marketYear, strictLocalize, latestMarketEvent]);

  const latestMarketMainEventRangeDate = useMemo(() => {
    const { mainEventRangeDate } = getFaireMarketUtils(
      marketYear,
      strictLocalize
    );
    return mainEventRangeDate(strictLocalize, latestMarketEvent);
  }, [marketYear, strictLocalize, latestMarketEvent]);

  const retailerSegment = useMemo(() => {
    if (!isLoggedInRetailerOrBrandPreview) {
      return RetailerSegment.NOT_LOGGED_IN;
    }
    if (
      insiderMembershipState(getRetailer()) ===
      IRetailer.InsiderMembershipState.ELIGIBLE
    ) {
      return RetailerSegment.INSIDER_ELIGIBLE;
    }
    switch (insiderMembershipState(getRetailer())) {
      case IRetailer.InsiderMembershipState.CHURNED:
      case IRetailer.InsiderMembershipState.PAUSED:
      case IRetailer.InsiderMembershipState.UNPAID:
        return RetailerSegment.INSIDER_CHURNED;
      case IRetailer.InsiderMembershipState.ACTIVE:
        return RetailerSegment.INSIDER_ENROLLED;
      default:
        return RetailerSegment.NOT_INSIDER;
    }
  }, [isLoggedInRetailerOrBrandPreview]);

  const mainEventEndAt = useMemo(() => {
    return eventData.end_at
      ? eventData.end_at
      : MARKET_FALLBACKS(strictLocalize).endAtTimestamp;
  }, [eventData, strictLocalize]);

  const earlyAccessStartAt = useMemo(() => {
    return eventData.insider_early_access_start_at
      ? eventData.insider_early_access_start_at
      : MARKET_FALLBACKS(strictLocalize).earlyAccessStartAtTimestamp;
  }, [eventData, strictLocalize]);

  const isAcquisitionSurfaceEnabled = useMemo(() => {
    const brandPortalLaunchTime = timestampToMillis(
      eventData.brand_portal_launch_time
    );
    if (brandPortalLaunchTime === undefined) {
      return false;
    }
    return now >= brandPortalLaunchTime;
  }, [eventData.brand_portal_launch_time, now]);

  const hasEnded = now > mainEventEndAt;
  const hasEarlyAccesStarted = now > earlyAccessStartAt;
  const hasCountdownEnded = hasEnded;
  const showMarketLandingPage = !hasEnded && isAcquisitionSurfaceEnabled;

  const showFaireDirectBanner = showMarketLandingPage;

  const marketListLaunchAt = useMemo(() => {
    const marketListLaunchTime = timestampToMillis(
      eventData.market_list_launch_time
    );
    return marketListLaunchTime ?? -1;
  }, [eventData]);

  const isMarketListLaunched =
    marketListLaunchAt > -1 && now >= marketListLaunchAt && !hasEnded;

  const showAllSpecials =
    !hasEnded &&
    retailerSegment !== RetailerSegment.NOT_LOGGED_IN &&
    isMarketListLaunched;

  const showMarketList = showAllSpecials;

  const showBrandLandingPage =
    !hasEarlyAccesStarted && isAcquisitionSurfaceEnabled;

  const preShowLaunchAt = useMemo(() => {
    const preShowLaunchTime = timestampToMillis(
      eventData.retailer_pre_show_launch_time
    );
    return preShowLaunchTime ?? -1;
  }, [eventData]);

  const mainEventStartAt = useMemo(() => {
    return eventData.start_at
      ? eventData.start_at
      : MARKET_FALLBACKS(strictLocalize).mainEventStartAtTimestamp;
  }, [eventData, strictLocalize]);

  const showPreshowTreatment = useMemo(() => {
    const endTime = eventData.end_at;
    if (preShowLaunchAt === -1 || endTime === undefined) {
      return false;
    }
    return now >= preShowLaunchAt && now < endTime;
  }, [preShowLaunchAt, eventData.end_at, now]);

  const _currentState = useMemo(() => {
    if (!mainEventStartAt || !mainEventEndAt || !earlyAccessStartAt) {
      return MarketState.DISABLED;
    }

    if (
      isWithinInterval(now, {
        start: earlyAccessStartAt,
        end: mainEventStartAt,
      })
    ) {
      return MarketState.EARLY_ACCESS;
    }

    if (
      isWithinInterval(now, {
        start: mainEventStartAt,
        end: mainEventEndAt,
      })
    ) {
      return MarketState.MAIN_EVENT;
    }

    return MarketState.DISABLED;
  }, [mainEventStartAt, mainEventEndAt, earlyAccessStartAt, now]);

  const currentState = useMemo(() => {
    if (retailerSegment === RetailerSegment.NOT_LOGGED_IN) {
      return MarketState.DISABLED;
    }
    return _currentState;
  }, [retailerSegment, _currentState]);

  const currentStateIncludingLoggedOut = _currentState;

  const isLive = useMemo(() => {
    switch (currentState) {
      case MarketState.EARLY_ACCESS:
        return retailerSegment === RetailerSegment.INSIDER_ENROLLED;
      case MarketState.MAIN_EVENT:
        return true;
      case MarketState.DISABLED:
        return false;
      default:
        return exhaustiveSwitchWithDefault(false)(currentState);
    }
  }, [currentState, retailerSegment]);

  const isPreshowOrLive = showPreshowTreatment || isLive;
  const showNavHeader = showMarketList;
  const isEarlyAccessDay = _currentState === MarketState.EARLY_ACCESS;
  const isInsiderDay =
    retailerSegment === RetailerSegment.INSIDER_ENROLLED && isEarlyAccessDay;

  const nextStateStartAt = useMemo(() => {
    switch (currentState) {
      case MarketState.DISABLED:
        if (now < earlyAccessStartAt) {
          return earlyAccessStartAt;
        }
        return 0;
      case MarketState.EARLY_ACCESS:
        return mainEventStartAt;
      case MarketState.MAIN_EVENT:
        return mainEventEndAt;
      default:
        return exhaustiveSwitchWithDefault(0)(currentState);
    }
  }, [currentState, earlyAccessStartAt, mainEventStartAt, mainEventEndAt, now]);

  const nextStateStartAtBasedInsiderEnrollment = useMemo(() => {
    if (
      retailerSegment !== RetailerSegment.INSIDER_ENROLLED &&
      retailerSegment !== RetailerSegment.INSIDER_ELIGIBLE &&
      currentState === MarketState.DISABLED &&
      now < earlyAccessStartAt
    ) {
      return mainEventStartAt;
    }
    return nextStateStartAt;
  }, [
    retailerSegment,
    currentState,
    now,
    earlyAccessStartAt,
    nextStateStartAt,
    mainEventStartAt,
  ]);

  const formattedEarlyAccessStartDay = useMemo(() => {
    const marketUtils = getFaireMarketUtils(undefined, strictLocalize);
    return marketUtils.formatDateInMarketTimezone(earlyAccessStartAt);
  }, [strictLocalize, earlyAccessStartAt]);

  const formattedLastDay = useMemo(() => {
    const marketUtils = getFaireMarketUtils(undefined, strictLocalize);
    return marketUtils.formatDateInMarketTimezone(mainEventEndAt);
  }, [strictLocalize, mainEventEndAt]);

  const actualStartAt = useMemo(() => {
    return eventData.start_at
      ? eventData.start_at
      : MARKET_FALLBACKS(strictLocalize).mainEventStartAtTimestamp;
  }, [eventData, strictLocalize]);

  const startAt = useMemo(() => {
    switch (retailerSegment) {
      case RetailerSegment.INSIDER_ENROLLED:
        return earlyAccessStartAt;
      default:
        return mainEventStartAt;
    }
  }, [retailerSegment, earlyAccessStartAt, mainEventStartAt]);

  const eventDaysPassed = Math.max(
    0,
    differenceInDays(now, actualStartAt.valueOf())
  );

  const eventDaysLeft = Math.max(
    0,
    differenceInDays(mainEventEndAt.valueOf(), now)
  );

  const matchedDiscountCapBps = useMemo(() => {
    const marketEventMatchedDiscountBps = getMarketEventMatchedDiscountBps();
    return marketEventMatchedDiscountBps === 0
      ? 500
      : marketEventMatchedDiscountBps;
  }, []);

  const marketSpecialsRoute = useMemo(() => {
    return marketSpecials(currentEventType);
  }, [currentEventType]);

  const isLiveForCountdownBanner = isLive;
  const deadlineForCountdownBanner = nextStateStartAtBasedInsiderEnrollment;
  const showCountdownBanner = isPreshowOrLive;

  const [hasMarketCountdownBanner, setHasMarketCountdownBanner] =
    useState(false);

  const isShowingCountdownBanner =
    isLoggedInRetailer && hasMarketCountdownBanner && showCountdownBanner;

  const isPreOrderExcludedForBrandPromo = useCallback(
    (
      eventKey: string | undefined,
      shipAvailability:
        | keyof typeof IBrandPromoCollection.ProductShipAvailability
        | undefined
    ): boolean => {
      return (
        eventKey !== undefined &&
        shipAvailability ===
          IBrandPromoCollection.ProductShipAvailability
            .ONLY_IMMEDIATE_PRODUCTS_DISCOUNTED
      );
    },
    []
  );

  const unsnapFromPT = useCallback((timeUtc: number): number => {
    return toZonedTime(timeUtc, PT_TIMEZONE).valueOf();
  }, []);

  return {
    currentEventType,
    marketName,
    latestMarketEarlyAccessRangeDate,
    latestMarketEarlyAccessRangeDateWithYear,
    latestMarketMainEventRangeDate,
    retailerSegment,
    showFaireDirectBanner,
    showAllSpecials,
    showMarketList,
    showMarketLandingPage,
    showBrandLandingPage,
    showPreshowTreatment,
    isPreshowOrLive,
    showNavHeader,
    isInsiderDay,
    currentState,
    currentStateIncludingLoggedOut,
    isEarlyAccessDay,
    nextStateStartAt,
    preShowLaunchAt,
    isMarketListLaunched,
    earlyAccessStartAt,
    formattedEarlyAccessStartDay,
    formattedLastDay,
    mainEventStartAt,
    mainEventEndAt,
    startAt,
    isLive,
    hasEnded,
    hasCountdownEnded,
    eventDaysPassed,
    eventDaysLeft,
    matchedDiscountCapBps,
    marketSpecialsRoute,
    isLiveForCountdownBanner,
    deadlineForCountdownBanner,
    showCountdownBanner,
    hasMarketCountdownBanner,
    setHasMarketCountdownBanner,
    isShowingCountdownBanner,
    isPreOrderExcludedForBrandPromo,
    unsnapFromPT,
  };
};
